import { getProjectGridSubcontractTotal } from '../../core/utilities/projectGrid/getProjectGridSubcontractTotal';
import type { IProjectGridRow, ISubcontractGridRow } from '../../types';

// Project total progress is based on these articles
//
// https://www.syncfusion.com/forums/147764/empty-progress-without-dates
// https://advisicon.com/percent-complete-microsoft-project/
//
// > In Gantt, we are calculated parent progress based on MS Project behavior.
// > Progress of parent task was calculated based on all scheduled child task's
// > progress and duration value. In Gantt, scheduled tasks are task which has
// > value for start date and end date or start date and duration or end date
// > and duration. Parent task progress was calculated as sum of all scheduled
// > task's progress divided by sum of all scheduled tasks duration value.
//
// Here, progress is weighted by each task's budget amount instead of duration.
//
// A row's own agg.budget only reflects resources assigned directly to that row - it is
// never a rollup of its descendants (see calculateProjectGridRowAggregates). Budget is
// typically assigned on leaf tasks only, so a parent/summary row's own budget is 0. If we
// weighted a row by its own budget alone, every row with children would drop out of the
// weighted average (0 weight), which is wrong when rolling up parents or totaling the
// whole project. So we recurse into children and include their budget too.
function getTaskBudgetValue(
  projectGridRow: IProjectGridRow,
  subcontractGridRowDict: Partial<Record<string, ISubcontractGridRow>>,
  rowDict: Partial<Record<string, IProjectGridRow>>
): number {
  const { personnel, equipment, expense } = projectGridRow.agg.budget;

  // agg.budget.subcontract/estimate is a cached snapshot that goes stale whenever the
  // linked subcontract grid rows change without this row's agg being recalculated, so
  // subcontract budget is always recomputed live from the current subcontract grid rows.
  const subcontractGridRows = projectGridRow.subcontractGridRowIds
    .map((id) => subcontractGridRowDict[id])
    .filter((row): row is ISubcontractGridRow => !!row);
  const contractSubcontractGridRows = subcontractGridRows.filter((row) => row.subcontractorId !== null);
  const estimateSubcontractGridRows = subcontractGridRows.filter((row) => row.subcontractorId === null);

  const subcontract = getProjectGridSubcontractTotal('budget', contractSubcontractGridRows);
  const estimate = getProjectGridSubcontractTotal('budget', estimateSubcontractGridRows, true);

  const ownValue = personnel + equipment + expense + subcontract + estimate;

  const childrenValue = projectGridRow.children.reduce((sum, childId) => {
    const child = rowDict[childId];
    return child ? sum + getTaskBudgetValue(child, subcontractGridRowDict, rowDict) : sum;
  }, 0);

  return ownValue + childrenValue;
}

export function calculateWorkPercentComplete(
  projectGridRows: IProjectGridRow[],
  subcontractGridRowDict: Partial<Record<string, ISubcontractGridRow>>,
  rowDict: Partial<Record<string, IProjectGridRow>>
): number {
  const { taskBudgetValue, completedBudget } = projectGridRows.reduce(
    (acc, projectGridRow) => {
      const taskBudgetValue = getTaskBudgetValue(projectGridRow, subcontractGridRowDict, rowDict);
      const percentComplete = projectGridRow.percentComplete ?? 0;
      if (taskBudgetValue > 0) {
        return {
          taskBudgetValue: acc.taskBudgetValue + taskBudgetValue,
          completedBudget: acc.completedBudget + (taskBudgetValue * percentComplete) / 100,
        };
      }

      return acc;
    },
    { taskBudgetValue: 0, completedBudget: 0 }
  );

  return taskBudgetValue ? Math.round((completedBudget / taskBudgetValue) * 100) : 0;
}
